<template>
  <!-- Header Card Island -->
  <header id="header"
    class="relative mx-3 mb-2 mt-3 flex h-14 items-center justify-between rounded-lg bg-background-card px-3 shadow-card sm:mx-4 sm:mt-4 sm:px-4">
    <div class="flex min-w-0 items-center gap-2 sm:gap-4">
      <!-- Navigation Menu Button (moved to left) -->
      <NavigationMenu />
      
      <!-- ADAM Cloud Logo (simplified to just image) -->
      <div class="hidden items-center sm:flex">
        <img src="/adam-cloud.png" alt="ADAM Cloud Logo" class="h-7 w-7" />
      </div>

      <template v-if="showProjectContext">
        <div class="hidden h-8 w-px bg-border lg:block"></div>

        <div
          class="hidden min-w-0 max-w-[16rem] items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 lg:flex"
          :title="`Current project: ${currentProject?.name} (${projectStatusLabel})`"
        >
          <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <i :class="[currentProject?.icon || 'fa-solid fa-folder-open', 'text-xs']"></i>
          </div>

          <div class="min-w-0 flex flex-1 items-center gap-2">
            <p class="truncate text-sm font-semibold text-primary">
              {{ currentProject?.name }}
            </p>
            <span :class="projectStatusClass">
              {{ projectStatusLabel }}
            </span>
          </div>
        </div>
      </template>

      <template v-if="currentNavItem">
        <div class="hidden h-8 w-px bg-border lg:block"></div>

        <!-- Current Navigation Icon and Name -->
        <div class="hidden min-w-0 items-center gap-2 text-primary lg:flex">
          <i :key="currentNavIcon" :class="['fa-solid', currentNavIcon, 'text-lg']"></i>
          <span class="truncate text-sm font-medium">{{ currentNavLabel }}</span>
        </div>
      </template>
    </div>

    <div class="flex items-center gap-4">
      <!-- Sync Indicator -->
      <div class="flex items-center">
        <!-- <template v-if="syncState.isSyncing">
          <i class="fa-solid fa-arrows-rotate animate-spin text-primary text-lg mr-2"></i>
          <span class="text-sm text-primary font-medium">Syncing...</span>
        </template>
        <template v-else>
          <i class="fa-solid fa-check text-green-500 text-lg mr-2"></i>
          <span class="text-sm text-green-600 font-medium">Synced <span v-if="syncState.lastSynced">{{ syncState.lastSynced }}</span></span>
        </template> -->
      </div>

      <!-- User Avatar/Name/Role block (not a button, just a flex row) -->
      <button 
        class="mb-0 flex items-center rounded-full px-2 py-2 transition-colors hover:bg-background-hover focus:outline-hidden focus:ring-2 focus:ring-primary sm:px-3"
        @click="router.push({ name: '/user-profile/' })"
      >
        <Avatar :label="userInitials" shape="circle" :class="userAvatarColor + ' h-9 w-9 text-sm font-bold text-white sm:h-10 sm:w-10'" />
        <div class="ml-3 hidden min-w-0 flex-col items-center justify-center md:flex" style="max-width: 10rem; width: auto;">
          <p
            class="text-center truncate"
            :style="userName.length > 18 ? { fontSize: Math.max(0.75, 1.2 - (userName.length - 15) * 0.04) + 'rem', maxWidth: '10rem' } : { maxWidth: '10rem' }"
            :title="userName"
          >
            {{ userName }}
          </p>
          <span :class="header.getRoleClass(userRole)">{{ $pascalToHuman(userRole) }}</span>
        </div>
      </button>
    </div>
  </header>
</template>

<script setup lang="ts">
import NavigationMenu from './NavigationMenu.vue';
import { computed } from 'vue';
import { useHeader } from '@/shared/composables/useHeader';
import router from '@/router';
import { useProfileStore } from '@/shared/stores/profile';
import Avatar from 'primevue/avatar';
import { useUserAvatar } from '@/shared/composables/useUserAvatar';
import { useUserRole } from '@/shared/composables/useUserRole';

const header = useHeader();
const currentNavItem = header.currentNavItem;
const currentNavIcon = header.currentNavIcon;
const currentNavLabel = header.currentNavLabel;
const requiresProjectContext = header.requiresProjectContext;
const currentProject = header.currentProject;

// Sync state placeholder removed; feature currently unused

const profileStore = useProfileStore();

const userName = computed(() => profileStore.profile?.name || 'John Doe');
const { initials: userInitials, colorClass: userAvatarColor } = useUserAvatar(userName);

// User role
const { userRole } = useUserRole();

const showProjectContext = computed(() => requiresProjectContext.value && currentProject.value !== null);

const projectStatusLabel = computed(() => currentProject.value?.statusLabel ?? currentProject.value?.status ?? 'Selected');

const projectStatusClass = computed(() => {
  switch (currentProject.value?.status) {
    case 'Active':
      return 'shrink-0 rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-medium text-success';
    case 'Pending':
      return 'shrink-0 rounded-full bg-warning/10 px-2 py-0.5 text-[11px] font-medium text-warning';
    case 'Template':
      return 'shrink-0 rounded-full bg-accent/10 px-2 py-0.5 text-[11px] font-medium text-accent';
    case 'Archived':
    default:
      return 'shrink-0 rounded-full bg-secondary/10 px-2 py-0.5 text-[11px] font-medium text-secondary';
  }
});
</script>

<style scoped>
/* No additional styles needed, Tailwind handles most */
</style>
